import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { ActivityLogConnectionSetupRequest } from '~src/settings/IntegrationsSettings/serverApi/requests/ActivityLogConnectionSetupRequest';
import { ISiteCEPPStatusResult, SiteCEPPConfiguration } from '~src/settings/IntegrationsSettings/serverApi/types/IActivityLog';

import { activityLogConnectionCancelButton, activityLogConnectionSetupButtons, activityLogConnectionSetupSpacer, activityLogConnectionSetupTextFieldSize, activityLogConnectionSetupTextFieldSpacer } from '../styles/ActivityLogSettingsStyles';
import { ActivityLogConnectionSetupDialogBodyProps } from './ActivityLogConnectionSetupDialog';
import { ACTIVITY_LOG } from './ActivityLogSettings';

export function ActivityLogConnectionSetupDialogFields(props: ActivityLogConnectionSetupDialogBodyProps) {
    const [enteredAccountNumber, setEnteredAccountNumber] = React.useState('');

    function validateAccountNumber(accountNumber: string) {
        // Save the entered account number for later comparison with KMS key ARN account number
        setEnteredAccountNumber(accountNumber);
        const accountNumberRegex = new RegExp('\\d{12}');
        return !accountNumberRegex.test(accountNumber) ? Messages.invalidAwsAccountNumber() : null;
    }

    function validateBucketName(bucketName: string) {
        // AWS bucket naming rules: https://docs.aws.amazon.com/AmazonS3/latest/userguide/bucketnamingrules.html
        const validLengthCharsRegex = new RegExp('^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$');
        const ipAddressRegex = new RegExp('(\\d{1,3}\\.){3}\\d{1,3}$');

        return (!validLengthCharsRegex.test(bucketName) || ipAddressRegex.test(bucketName) || bucketName.startsWith('xn--') || bucketName.endsWith('-s3alias')) ? Messages.invalidAwsBucketName() : null;
    }

    function validateKmsKeyARN(arn: string) {
        // KMS key ARN format: arn:aws:kms:<region>:<account-id>:key/<key-id>
        const kmsKeyArnRegex = new RegExp('arn:aws:kms:([a-z0-9-]+):([0-9]+):key\/([a-fA-F0-9-]+)');
        return !kmsKeyArnRegex.test(arn) ? Messages.invalidKmsKeyArn() : validateAccountNumberMatchesArnAccountNumber(kmsKeyArnRegex.exec(arn)[2]);
    }

    function validateAccountNumberMatchesArnAccountNumber(arnAccountNumber: string) {
        // Account number section of the ARN must be the same account number entered in the first field
        return arnAccountNumber !== enteredAccountNumber ? Messages.awsAccountNumberAndKeyAccountNumberMismatch() : null;
    }

    function submitSetupInformation(values: SiteCEPPConfiguration, formik: FormikHelpers<any>) {
        formik.setSubmitting(true);
        WebHttpClient.sendRequest(new ActivityLogConnectionSetupRequest(values)).then((result: ISiteCEPPStatusResult) => {
            formik.setSubmitting(false);

            if (result.error) {
                // If request is unsuccessful, keep widget open and render error section
                // TODO (TFS1373415): Confirm error message to render; currently using the wording returned from server
                props.error(result.error);
                formik.resetForm();
            } else {
                // If request is successful, close widget and render status widget
                props.updateStatusCallback(result);
                props.close({});
            }
        });
    }

    return (
        <Formik
            initialValues={props.configuration}
            onSubmit={(values: SiteCEPPConfiguration, formik: FormikHelpers<any>) => {
                submitSetupInformation(values, formik);
            }}
            enableReinitialize={true}
        >
            {(formik: FormikProps<SiteCEPPConfiguration>) => (
                <div>
                    <span>
                        <form name='connectionSetupForm' autoComplete='off' noValidate onSubmit={formik.handleSubmit} data-tb-test-id={`${ACTIVITY_LOG}-connection-setup-form`}>
                            <div>
                                <FormikTextBox
                                    testId={`${ACTIVITY_LOG}-dialog-aws-acct-num-tb`}
                                    label={Messages.awsAccountNumber()}
                                    validate={validateAccountNumber}
                                    showValidationMessage={true}
                                    eagerValidation={false}
                                    name='awsAccountNumber'
                                    tabIndex={0}
                                    placeholder={''}
                                    size={activityLogConnectionSetupTextFieldSize}
                                />
                                <div className={activityLogConnectionSetupTextFieldSpacer}>&nbsp;</div>
                                <FormikTextBox
                                    testId={`${ACTIVITY_LOG}-dialog-bucket-name-tb`}
                                    label={Messages.s3BucketName()}
                                    validate={validateBucketName}
                                    showValidationMessage={true}
                                    eagerValidation={false}
                                    name='s3BucketName'
                                    tabIndex={0}
                                    size={activityLogConnectionSetupTextFieldSize}
                                />
                                <div className={activityLogConnectionSetupTextFieldSpacer}>&nbsp;</div>
                                <FormikTextBox
                                    testId={`${ACTIVITY_LOG}-dialog-kms-key-arn-tb`}
                                    label={Messages.kmsKeyArn()}
                                    validate={validateKmsKeyARN}
                                    showValidationMessage={true}
                                    eagerValidation={false}
                                    name='kmsKeyARN'
                                    tabIndex={0}
                                    size={activityLogConnectionSetupTextFieldSize}
                                />
                                <div className={activityLogConnectionSetupSpacer}>&nbsp;</div>
                                <div className={activityLogConnectionSetupButtons}>
                                    <div className={activityLogConnectionCancelButton}>
                                        <ButtonWidget buttonType={ButtonType.Outline}
                                            type="button"
                                            testId={`${ACTIVITY_LOG}-dialog-cancel-btn`}
                                            onClick={() => props.cancel()}
                                            tabIndex={0}>
                                            {Messages.cancel()}
                                        </ButtonWidget>
                                    </div>
                                    <ButtonWidget buttonType={ButtonType.Primary}
                                        type="submit"
                                        testId={`${ACTIVITY_LOG}-dialog-submit-btn`}
                                        disabled={!formik.values.awsAccountNumber || !formik.values.s3BucketName || !formik.values.kmsKeyARN || !formik.isValid}
                                        tabIndex={0}>
                                        {Messages.submit()}
                                    </ButtonWidget>
                                </div>
                            </div>
                        </form>
                    </span>
                </div>
            )}
        </Formik>
    );
}
